/** @format */

import React from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import {
	Palette,
	Layers,
	Check,
	Sun,
	Moon,
	Eye,
	EyeOff,
	Wind,
	Users,
	ArrowRight,
	SwatchBook,
	ShieldCheck,
	Sparkles,
} from "lucide-react"; // Added relevant icons
import ContactBlock from "../../components/contact/ContactBlock"; // Assuming path

// --- Enhanced Fabric Data Structure ---
const fabricTypes = [
	{
		id: "blackout",
		type: "Blackout",
		shortDesc: "Complete light blockage for total darkness.",
		desc: "Ideal for bedrooms, media rooms, or anywhere maximum darkness and privacy are desired. Blocks 100% of incoming light.",
		features: [
			{ text: "Total Darkness", icon: Moon },
			{ text: "Maximum Privacy", icon: EyeOff },
			{ text: "UV Protection", icon: ShieldCheck }, // Assuming ShieldCheck exists or use placeholder
			{ text: "Energy Efficient", icon: Wind }, // Represents insulation
		],
		bestFor: ["Bedrooms", "Media Rooms", "Nurseries"],
		image: "/images/selection/blackout.jpg", // Use an image showing the effect in a room
		ctaLink: "/shop?lightControl=Blackout", // Link to shop pre-filtered
		ctaText: "Shop Blackout Fabrics",
	},
	{
		id: "privacy",
		type: "Privacy / Room Darkening", // Clearer combined term
		shortDesc: "Significant light blocking while ensuring privacy.",
		desc: "These fabrics darken the room considerably and prevent visibility from the outside, day or night. Silhouettes may be slightly visible with interior lights on at night.",
		features: [
			{ text: "High Privacy", icon: EyeOff },
			{ text: "Room Darkening", icon: Moon }, // Use Moon with maybe lower opacity? Or a specific Dim icon
			{ text: "Softly Diffused Light", icon: Sun }, // Sun icon indicating some light passes
			{ text: "Variety of Textures", icon: Layers },
		],
		bestFor: ["Living Rooms", "Bedrooms", "Bathrooms"],
		image: "/images/selection/privacy.jpg",
		ctaLink: "/shop?lightControl=Privacy",
		ctaText: "Shop Privacy Fabrics",
	},
	{
		id: "translucent",
		type: "Light Filtering (Translucent)", // Add clarifying term
		shortDesc: "Softly diffuses light while maintaining privacy.",
		desc: "Creates a warm, inviting glow by gently filtering sunlight. Provides privacy during the day; shadows may be visible at night with lights on.",
		features: [
			{ text: "Moderate Privacy", icon: EyeOff }, // Still provides privacy
			{ text: "Diffused Natural Light", icon: Sun },
			{ text: "Reduces Glare", icon: Sun }, // Maybe a glare icon?
			{ text: "Textured Options", icon: Layers },
		],
		bestFor: ["Living Rooms", "Dining Rooms", "Offices"],
		image: "/images/selection/translucent.jpg",
		ctaLink: "/shop?lightControl=Translucent",
		ctaText: "Shop Light Filtering Fabrics",
	},
	{
		id: "sunscreen",
		type: "Solar / Sunscreen", // Use common terms
		shortDesc: "Preserves view while reducing heat & glare.",
		desc: "Performance fabrics designed to control heat, glare, and UV rays while maintaining your outside view. Openness factor (e.g., 1%, 3%, 5%) determines visibility and light control.",
		features: [
			{ text: "Maintains View", icon: Eye },
			{ text: "Reduces Heat & Glare", icon: Sun }, // Could use Temperature icon
			{ text: "UV Protection", icon: ShieldCheck },
			{ text: "Daytime Privacy", icon: EyeOff }, // Privacy during day, less at night
		],
		bestFor: ["Offices", "Sunrooms", "Living Areas with Views"],
		image: "/images/selection/solar.jpg",
		ctaLink: "/shop?lightControl=Solar",
		ctaText: "Shop Solar Fabrics",
	},
	{
		id: "transparent",
		type: "Sheer / Transparent", // Use common terms
		shortDesc: "Maximizes natural light and visibility.",
		desc: "Lightweight, decorative fabrics that allow the most natural light in and offer the clearest view outside. Provides minimal privacy.",
		features: [
			{ text: "Clear View Out", icon: Eye },
			{ text: "Maximum Natural Light", icon: Sun },
			{ text: "Decorative Styles", icon: Sparkles }, // Assuming Sparkles exists
			{ text: "Minimal Privacy", icon: Eye }, // Explicitly state minimal privacy
		],
		bestFor: ["Living Rooms", "Dining Rooms", "Areas where privacy is not key"],
		image: "/images/selection/transparent.jpg", // Sheer fabrics often called Transparent too
		ctaLink: "/shop?lightControl=Transparent", // Or lightControl=Sheer if that's used
		ctaText: "Shop Sheer Fabrics",
	},
	// Removed 'Privacy Roller Shades' as a separate entry, merged concept with 'Room Darkening'
];

// --- Reusable Fabric Type Card ---
const FabricTypeCard = ({ fabric, delay }) => (
	<motion.div
		key={fabric.id}
		className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden flex flex-col h-full" // Ensure full height for flex
		initial={{ opacity: 0, y: 30 }}
		whileInView={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.4, delay: delay, ease: "easeOut" }}
		viewport={{ once: true, amount: 0.2 }}
	>
		{/* Image */}
		<div className="aspect-video bg-gray-100 overflow-hidden">
			{/* Use an image that clearly demonstrates the light effect */}
			<img
				src={fabric.image}
				alt={`${fabric.type} Fabric Effect`}
				className="w-full h-full object-cover"
			/>
		</div>
		{/* Content */}
		<div className="p-5 md:p-6 flex flex-col flex-grow">
			<h3 className="text-xl font-semibold text-gray-800 mb-2">
				{fabric.type}
			</h3>
			<p className="text-sm text-gray-600 mb-4">{fabric.desc}</p>
			{/* Features */}
			<div className="space-y-1.5 mb-4">
				<h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
					Key Features:
				</h4>
				{fabric.features.map((feature, fIndex) => (
					<div
						key={fIndex}
						className="flex items-center gap-2 text-sm text-gray-700"
					>
						<feature.icon
							size={16}
							className="text-primary flex-shrink-0"
							strokeWidth={2}
						/>
						<span>{feature.text}</span>
					</div>
				))}
			</div>
			{/* Best For */}
			<div className="space-y-1.5 mb-5">
				<h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
					Ideal For:
				</h4>
				<p className="text-sm text-gray-700">{fabric.bestFor.join(", ")}</p>
			</div>
			{/* Call to Action */}
			<Link
				to={fabric.ctaLink || "/shop"} // Fallback link
				className="flex items-center mt-auto block w-full btn btn-primary-outline text-sm py-2" // Use outline style for card CTA
			>
				{fabric.ctaText || "View Fabrics"} <ArrowRight size={16} />
			</Link>
		</div>
	</motion.div>
);

// --- Main Guide Screen ---
const ProductSelectionGuideScreen = () => {
	return (
		<div className="overflow-x-hidden bg-gray-50">
			<Helmet>
				<title>
					Fabric Selection Guide: Choosing Light Control & Privacy | Off Cut
					Shades
				</title>{" "}
				{/* SEO Optimized Title */}
				<meta
					name="description"
					content="Understand the difference between Blackout, Privacy, Light Filtering, Solar, and Sheer fabrics to choose the perfect roller shades for your needs. Learn about light control and privacy levels."
				/>{" "}
				{/* SEO Optimized Desc */}
				<link
					rel="canonical"
					href="https://offcutshades.com/guides/productselection"
				/>{" "}
				{/* Updated Canonical */}
			</Helmet>

			<main>
				{/* --- Hero Section --- */}
				<section className="bg-gradient-to-b from-white to-gray-50 pt-24 pb-16 md:pt-32 md:pb-20 text-center">
					<div className="container mx-auto px-4 max-w-3xl">
						<motion.div
							initial={{ scale: 0.8, opacity: 0 }}
							animate={{ scale: 1, opacity: 1 }}
							transition={{ type: "spring", stiffness: 150, delay: 0.1 }}
							className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 text-primary mb-4"
						>
							<Palette size={36} strokeWidth={1.5} /> {/* Icon */}
						</motion.div>
						<motion.h1
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.2 }}
							className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
						>
							Choosing Your Perfect Fabric
						</motion.h1>
						<motion.p
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.3 }}
							className="text-lg text-gray-600 max-w-2xl mx-auto"
						>
							Find the right balance of light, privacy, and style. This guide
							explains our fabric types to help you decide.
						</motion.p>
					</div>
				</section>

				{/* --- Fabric Types Grid --- */}
				<section className="container mx-auto px-4 py-12 md:py-16">
					<h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-8 md:mb-10 text-center">
						Understanding Fabric Options
					</h2>
					{/* Use a responsive grid */}
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
						{fabricTypes.map((fabric, index) => (
							<FabricTypeCard
								key={fabric.id}
								fabric={fabric}
								delay={index * 0.1}
							/>
						))}
					</div>
				</section>

				{/* --- Next Steps Section --- */}
				<section className="container mx-auto px-4 py-12 md:py-16 text-center bg-gray-100 rounded-xl my-16">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.5 }}
					>
						<h2 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-4">
							Ready to See More?
						</h2>
						<p className="text-gray-600 mb-6 max-w-xl mx-auto">
							Now that you understand the fabric types, explore specific colours
							and textures or order free samples!
						</p>
						<div className="flex flex-col sm:flex-row justify-center gap-4">
							<Link to="/swatches" className="btn btn-secondary">
								{" "}
								{/* Use secondary style */}
								<SwatchBook size={18} /> Order Free Samples
							</Link>
							<Link to="/shop" className="btn btn-primary">
								{" "}
								{/* Primary action */}
								Shop All Shades <ArrowRight size={18} />
							</Link>
						</div>
					</motion.div>
				</section>
			</main>

			{/* --- Contact Support Section --- */}
			<ContactBlock
				title="Still Unsure?"
				description="Our shade experts are happy to help you choose the perfect fabric. Get in touch for personalized recommendations."
			/>
		</div>
	);
};

// Add button styles if not global
/*
@layer components {
  .btn { @apply px-5 py-2.5 rounded-lg font-semibold text-center shadow-sm transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed; }
  .btn-primary { @apply bg-primary text-white hover:bg-primary/90; }
  .btn-secondary { @apply bg-gray-600 text-white hover:bg-gray-700; }
  .btn-primary-outline { @apply bg-white text-primary border-2 border-primary hover:bg-primary/10; }
}
*/

export default ProductSelectionGuideScreen;
